/**
 * Hero product captures for /features — two or three per role, no more.
 *
 * IMPORTANT — this used to be a per-menu-item map of 132 real screenshots, one
 * for every screen in the product, keyed by the sidebar's own `key` values. Any
 * visitor could click through the rail and collect a complete visual spec of
 * Kaympus, and the map itself shipped in the client bundle, so a competitor did
 * not even have to click. That is the thing this file now deliberately does not
 * do — see the header of featureData.ts for the full rule.
 *
 * What stays: one representative capture per role, enough to prove the product
 * is real and looks finished. Everything else falls back to the synthetic MOCKS
 * panel, which shows the *shape* of a screen without publishing the screen.
 *
 * Keyed by group, not by item: every item in a group resolves to that group's
 * hero shot. Per-item mapping is what made the old file a spec, and there is no
 * reason to reintroduce it.
 *
 * Before adding an entry, ask whether a rival could build from it. If yes, do
 * not add it — put it in the demo deck instead.
 *
 * Values are paths under `public/screenshots/`, minus the extension. Desktop
 * captures are served at 1220×854 — shot at 1440×900, then cut twice.
 *
 * x≥220 drops the app's own nav menu, because this page deliberately stopped
 * publishing that tree. y<854 drops the app footer: the admin shell pins it to
 * the bottom of the viewport, so the last 46 rows of every raw capture are a
 * divider rule and a white "Copyright © 2026 - Maple Ridge Public School" band
 * sitting on top of content that scrolls on below it. In the window mockup that
 * band reads as this site's own footer leaking into the product shot.
 *
 * Both crops are baked into the file, not applied in CSS: a CSS crop leaves the
 * full image downloadable at its URL. Mobile captures are 390×844 — a phone app
 * with neither a nav rail nor a web footer — and are served uncut.
 *
 * The shape is in the filename because FeatureCatalogue hard-codes it as the
 * <img> width/height, and /screenshots is plain static hosting with no content
 * hash: re-cutting one of these in place would let a cached 900-tall copy be
 * served into a box the code declares as 854. Never overwrite one — change the
 * name and delete the file you replaced. Hero.tsx keeps its own asset at its
 * own name (dashboard-hero-1220x855-v2) for the same reason.
 *
 * Only the files referenced here (plus the two used by Hero.tsx) are meant to
 * exist; anything left under public/ stays fetchable at its URL whether or not
 * a page links to it.
 */
const HERO_SHOTS: Record<string, string> = {
  // Admin — command center, records, academics, finance, and campus operations
  "admin-overview": "admin/dashboard-1220x854",
  "admin-people": "admin/students-1220x854",
  "admin-academics": "admin/report-cards-1220x854",
  "admin-finance": "admin/fees-collection-1220x854",
  "admin-operations": "admin/notice-1220x854",

  // Teacher — daily schedule, classroom assessment, mobile app, and payroll/leave
  "teacher-day": "teacher/dashboard-1220x854",
  "teacher-classroom": "teacher/attendance-1220x854",
  "teacher-app": "mobile/teacher-home",
  "teacher-self": "teacher/my-payslips-1220x854",

  // Staff — workspace dashboard, mobile punch-in, front desk/fees, and facilities
  "staff-workspace": "staff/dashboard-1220x854",
  "staff-app": "mobile/staff-punch",
  "staff-frontdesk": "staff/fees-collection-1220x854",
  "staff-facilities": "staff/library-1220x854",

  // Student & Parent — family portal, mobile app, exams & results, and fee portal
  "student-home": "student/dashboard-1220x854",
  "student-app": "mobile/student-home",
  "student-academics": "student/exam-results-1220x854",
  "student-fees": "student/my-fees-1220x854",
};

export type Screenshot = {
  /** Public path, e.g. /screenshots/admin/dashboard-1220x854.webp */
  src: string;
  alt: string;
  /** True for 390×844 phone captures, which need the device frame. */
  isMobile: boolean;
};

/**
 * The hero capture for a group, or null to fall back to the synthetic mock.
 *
 * A null here is the normal case, not a gap to be filled: most groups are meant
 * to show the mock.
 */
export function getScreenshot(groupId: string, label: string): Screenshot | null {
  const path = HERO_SHOTS[groupId];
  if (!path) return null;

  return {
    src: `/screenshots/${path}.webp`,
    /* Described by the capability the visitor selected rather than by the file,
       so the alt text tracks the rail instead of naming an internal screen. */
    alt: `Kaympus ${label}`,
    isMobile: path.startsWith("mobile/"),
  };
}
